import React, { useState, useCallback } from "react";
import { useLocation, matchRoutes } from "react-router-dom";

import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { ChangePasswordGate } from "./ChangePasswordGate";
import { FrozenStudentGate } from "./FrozenStudentGate";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { SubscriptionBanner } from "../../features/plan/components/SubscriptionBanner";
import { useSaasSubscription } from "../../features/plan/hooks/useSaasSubscription";
import {
  buildSuperAdminNavItems,
  isSuperAdminPath,
} from "../../features/superadmin/navItems";
import { registry } from "../router/registry";
import { useAuth } from "../providers/AuthProvider";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/tokens.css";
import "./AppLayout.css";
// Debe ir al final: repinta la paleta de Bootstrap (primary/estados) a Diezlar.
import "../../styles/brand.css";

/**
 * Build tenant-scoped nav items from the authenticated user so links keep the
 * slug. Returns undefined when there is no slug (legacy/unauthed) → Sidebar then
 * falls back to its own legacy DEFAULT_NAV_ITEMS.
 */
function buildNavItems(user, activeRole, pathname) {
  // Panel de plataforma (TASK-22): la sidebar muestra las secciones de
  // superadmin que permite el rol de staff. NO es tenant-scoped — el staff de
  // plataforma no pertenece a ninguna academia y por tanto no tiene slug, así
  // que sin esta rama caería en el `return undefined` de abajo y la sidebar
  // saldría vacía.
  if (isSuperAdminPath(pathname)) return buildSuperAdminNavItems(user);

  const slug = user?.slug;
  if (!slug) return undefined;

  // El nav refleja la VISTA activa del switcher (rol dual), no todos los roles.
  const role = activeRole ?? user?.role;
  const base = `/${slug}/app`;
  const items = [
    { to: `${base}/dashboard`, icon: "mdi-view-dashboard-outline", label: "Inicio" },
  ];

  if (role === "OWNER") {
    items.push(
      // ── Dinero (trabajo diario) ──────────────────────────────────────────
      { to: `${base}/academia/dinero/pagos`, icon: "mdi-cash-register", label: "Pagos" },
      { to: `${base}/academia/dinero/cartera`, icon: "mdi-briefcase-outline", label: "Cartera" },
      { to: `${base}/academia/dinero/retencion`, icon: "mdi-account-alert-outline", label: "Retención" },
      { to: `${base}/academia/dinero/honorarios`, icon: "mdi-cash", label: "Honorarios", requiresFeature: "has_honorarios" },
      // ── Gestión ──────────────────────────────────────────────────────────
      { to: `${base}/academia/clases`, icon: "mdi-calendar-multiple-check", label: "Clases" },
      { to: `${base}/academia/docentes`, icon: "mdi-account-multiple-outline", label: "Docentes" },
      { to: `${base}/academia/propietarios`, icon: "mdi-account-star-outline", label: "Propietarios" },
      { to: `${base}/academia/estudiantes`, icon: "mdi-account-group-outline", label: "Estudiantes" },
      { to: `${base}/academia/temas`, icon: "mdi-tag-multiple", label: "Temas" },
      { to: `${base}/academia/niveles`, icon: "mdi-stairs", label: "Niveles" },
      // ── Configuración de la academia ─────────────────────────────────────
      { to: `${base}/academia/paquetes`, icon: "mdi-package-variant-closed", label: "Paquetes" },
      { to: `${base}/academia/tarifas`, icon: "mdi-cash-multiple", label: "Tarifas" },
      { to: `${base}/academia/sedes`, icon: "mdi-map-marker-outline", label: "Sedes" },
      { to: `${base}/academia/aulas`, icon: "mdi-door", label: "Aulas" },
      { to: `${base}/academia/politicas`, icon: "mdi-file-document-outline", label: "Políticas" },
      { to: `${base}/plan`, icon: "mdi-crown", label: "Plan" }
    );
  }

  if (role === "TEACHER") {
    items.push(
      { to: `${base}/agenda`, icon: "mdi-calendar-clock", label: "Mi agenda" },
      { to: `${base}/mis-horas`, icon: "mdi-clock-outline", label: "Mis horas" }
    );
  }

  if (role === "STUDENT") {
    items.push(
      { to: `${base}/mi-espacio/mis-clases`, icon: "mdi-book-open-page-variant", label: "Mis clases" },
      { to: `${base}/mi-espacio/mi-saldo`, icon: "mdi-wallet-outline", label: "Saldo y pagos" }
      // "Mis pagos" se integró en "Saldo y pagos". "Mi progreso" se movió al perfil.
    );
  }

  return items;
}

/**
 * The new app shell is skipped when the matched route opts out via the registry:
 *   - `public: true`      → auth pages (login/registro/...) render bare.
 *   - `selfLayout: true`  → legacy pages that still bring their own BaseLayout
 *                           (avoids the double-sidebar during the migration).
 * Reading the flag from the registry keeps this in sync with the routes instead
 * of hardcoding path regexes.
 */
function hidesAppShell(pathname) {
  const matches = matchRoutes(registry.routes, pathname);
  if (!matches) return false;
  return matches.some(
    (m) => m.route.public === true || m.route.selfLayout === true
  );
}

/**
 * AppLayout — vertical-fixed shell (sin jQuery).
 *
 * Props:
 *   children   — routed page content (injected by TASK-04 router)
 *   navItems   — optional role-specific nav list (injected by TASK-04 providers)
 */
function AppLayout({ children, navItems }) {
  const { pathname } = useLocation();
  const { user, isLoading, roles, activeRole } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Feature-gate nav items: hide entries whose `requiresFeature` is off in the
  // owner's plan (e.g. Honorarios when the plan lacks has_honorarios). The
  // subscription endpoint is owner-scoped, so fetch it whenever the user HAS the
  // owner role (regardless of the active view in the switcher).
  const isOwner = roles.includes("OWNER");
  const { data: subscription } = useSaasSubscription({ enabled: isOwner });
  const planFeatures = subscription?.plan?.features;

  // Explicit navItems prop wins; otherwise derive slug-scoped items from the
  // user's ACTIVE view (the role switcher decides which menu shows).
  let resolvedNavItems = navItems ?? buildNavItems(user, activeRole, pathname);
  if (resolvedNavItems && planFeatures) {
    resolvedNavItems = resolvedNavItems.filter(
      (i) => !i.requiresFeature || planFeatures[i.requiresFeature]
    );
  }

  // Escritorio: contrae/expande el sidebar a solo-iconos. Móvil: abre/cierra el
  // drawer deslizable. El breakpoint 992px coincide con el del CSS.
  const toggleSidebar = useCallback(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 992) {
      setCollapsed((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  if (hidesAppShell(pathname)) {
    return <>{children}</>;
  }

  // While the session is hydrating (silent refresh + /users/me), render a neutral
  // loader instead of the shell. Otherwise the Sidebar would briefly fall back to
  // its legacy nav (Inicio defaults) on every F5 — a visible flicker in Chrome and
  // a stuck wrong layout in browsers where the refresh cookie is slow/blocked.
  if (isLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando…</span>
          </div>
          <div className="mt-2 text-muted">Cargando…</div>
        </div>
      </div>
    );
  }

  // Forced password rotation: a teacher created by an OWNER with a temporary
  // password cannot reach any app route until they set a new password.
  if (user?.must_change_password) {
    return <ChangePasswordGate />;
  }

  // Estudiante registrado por encima del límite: puede entrar pero solo ve el
  // aviso de "en espera de capacidad" hasta que la academia amplíe su cupo.
  if (user?.student_frozen) {
    return <FrozenStudentGate />;
  }

  return (
    <div className={`app-shell${collapsed ? " sidebar-collapsed" : ""}`}>
      {/* TASK-19 (a11y): keyboard users skip nav straight to the page content. */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido
      </a>
      <Navbar onToggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      {/* overlay for mobile — click to close */}
      <div
        className={`app-sidebar-overlay${sidebarOpen ? " is-open" : ""}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      <Sidebar
        isOpen={sidebarOpen}
        navItems={resolvedNavItems}
        onNavigate={closeSidebar}
      />

      <div className="app-main">
        <ImpersonationBanner />
        {isOwner && <SubscriptionBanner />}
        <main className="app-content" id="main-content" tabIndex={-1}>
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
}

export { AppLayout };
